import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@shared/contexts/AuthContext";
import { Button } from "@shared/components/ui/button";
import { Input } from "@shared/components/ui/input";
import {
  useCreateDepartment,
  useCreateTeam,
  useDeleteDepartment,
  useDeleteTeam,
  useDepartments,
  useTeams,
  useUpdateDepartment,
  useUpdateTeam,
} from "@shared/api/hooks/useOrg";
import { ApiError } from "@shared/api/client";
import type { Department } from "@shared/api/departments";
import type { Team } from "@shared/api/teams";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";

// F-A — admin-only Organization admin. Two panes: Departments + Teams.
// Inline create; per-row rename (prompt) + delete (confirm). Cross-entity
// 409s from the backend surface verbatim as error toasts.

const OrgAdmin = () => {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const { data: departments = [] } = useDepartments();
  const { data: teams = [] } = useTeams();

  const createDep = useCreateDepartment();
  const updateDep = useUpdateDepartment();
  const deleteDep = useDeleteDepartment();
  const createTeam = useCreateTeam();
  const updateTeam = useUpdateTeam();
  const deleteTeam = useDeleteTeam();

  const [newDep, setNewDep] = useState("");
  const [newTeam, setNewTeam] = useState("");
  const [newTeamDep, setNewTeamDep] = useState("");

  useEffect(() => {
    if (!isAdmin) {
      toast.error("Admin access required");
      navigate("/staff/exhibitions");
    }
  }, [isAdmin, navigate]);

  if (!isAdmin) return null;

  const apiErr = (err: unknown, fallback: string) =>
    toast.error(
      err instanceof ApiError && err.message ? err.message : fallback,
    );
  const depName = (depId: Team["departmentId"]) =>
    departments.find((d) => d.id === depId)?.name ?? "—";

  const addDepartment = () => {
    if (!newDep.trim()) return;
    createDep.mutate(
      { name: newDep.trim() },
      {
        onSuccess: () => {
          setNewDep("");
          toast.success("Department created");
        },
        onError: (err) => apiErr(err, "Failed to create department"),
      },
    );
  };

  const renameDepartment = (d: Department) => {
    const name = window.prompt("Department name:", d.name);
    if (!name || name === d.name) return;
    updateDep.mutate(
      { departmentId: d.id, payload: { name } },
      {
        onSuccess: () => toast.success("Department updated"),
        onError: (err) => apiErr(err, "Failed to update department"),
      },
    );
  };

  const removeDepartment = (d: Department) => {
    if (!window.confirm(`Delete department "${d.name}"?`)) return;
    deleteDep.mutate(d.id, {
      onSuccess: () => toast.success("Department deleted"),
      onError: (err) => apiErr(err, "Failed to delete department"),
    });
  };

  const addTeam = () => {
    if (!newTeam.trim()) return;
    createTeam.mutate(
      { name: newTeam.trim(), departmentId: newTeamDep || null },
      {
        onSuccess: () => {
          setNewTeam("");
          setNewTeamDep("");
          toast.success("Team created");
        },
        onError: (err) => apiErr(err, "Failed to create team"),
      },
    );
  };

  const renameTeam = (t: Team) => {
    const name = window.prompt("Team name:", t.name);
    if (!name || name === t.name) return;
    updateTeam.mutate(
      { teamId: t.id, payload: { name } },
      {
        onSuccess: () => toast.success("Team updated"),
        onError: (err) => apiErr(err, "Failed to update team"),
      },
    );
  };

  const removeTeam = (t: Team) => {
    if (!window.confirm(`Delete team "${t.name}"?`)) return;
    deleteTeam.mutate(t.id, {
      onSuccess: () => toast.success("Team deleted"),
      onError: (err) => apiErr(err, "Failed to delete team"),
    });
  };

  return (
    <div className="min-h-screen bg-background" data-testid="org-admin-page">
      <div className="bg-white border-b border-border">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center gap-3">
          <Button
            onClick={() => navigate("/staff/exhibitions")}
            variant="ghost"
            size="sm"
            data-testid="back-button"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <h1 className="text-2xl font-heading font-bold">Organization</h1>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8 grid gap-8 md:grid-cols-2">
        {/* Departments */}
        <section data-testid="departments-pane">
          <h2 className="text-lg font-medium mb-3">Departments</h2>
          <div className="flex gap-2 mb-4">
            <Input
              placeholder="New department name"
              value={newDep}
              onChange={(e) => setNewDep(e.target.value)}
              data-testid="new-department-input"
              className="h-10"
            />
            <Button onClick={addDepartment} data-testid="add-department-button">
              Add
            </Button>
          </div>
          <ul className="border border-border rounded-lg divide-y divide-border bg-white">
            {departments.length === 0 && (
              <li className="px-4 py-3 text-sm text-muted-foreground">
                No departments yet.
              </li>
            )}
            {departments.map((d) => (
              <li
                key={d.id}
                className="px-4 py-3 flex items-center justify-between"
                data-testid={`department-row-${d.id}`}
              >
                <span className="text-sm">{d.name}</span>
                <span className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => renameDepartment(d)}
                    data-testid={`edit-department-${d.id}`}
                  >
                    Edit
                  </Button>
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() => removeDepartment(d)}
                    data-testid={`delete-department-${d.id}`}
                  >
                    Delete
                  </Button>
                </span>
              </li>
            ))}
          </ul>
        </section>

        {/* Teams */}
        <section data-testid="teams-pane">
          <h2 className="text-lg font-medium mb-3">Teams</h2>
          <div className="flex flex-col gap-2 mb-4">
            <Input
              placeholder="New team name"
              value={newTeam}
              onChange={(e) => setNewTeam(e.target.value)}
              data-testid="new-team-input"
              className="h-10"
            />
            <div className="flex gap-2">
              <select
                value={newTeamDep}
                onChange={(e) => setNewTeamDep(e.target.value)}
                data-testid="new-team-department-select"
                className="h-10 flex-1 border border-input rounded-md px-3 bg-background"
              >
                <option value="">— no department —</option>
                {departments.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </select>
              <Button onClick={addTeam} data-testid="add-team-button">
                Add
              </Button>
            </div>
          </div>
          <ul className="border border-border rounded-lg divide-y divide-border bg-white">
            {teams.length === 0 && (
              <li className="px-4 py-3 text-sm text-muted-foreground">
                No teams yet.
              </li>
            )}
            {teams.map((t) => (
              <li
                key={t.id}
                className="px-4 py-3 flex items-center justify-between"
                data-testid={`team-row-${t.id}`}
              >
                <span className="text-sm">
                  {t.name}
                  <span className="text-muted-foreground">
                    {" "}
                    · {depName(t.departmentId)}
                  </span>
                </span>
                <span className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => renameTeam(t)}
                    data-testid={`edit-team-${t.id}`}
                  >
                    Edit
                  </Button>
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() => removeTeam(t)}
                    data-testid={`delete-team-${t.id}`}
                  >
                    Delete
                  </Button>
                </span>
              </li>
            ))}
          </ul>
        </section>
      </div>
    </div>
  );
};

export default OrgAdmin;
